
// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import { getDatabase} from "firebase/database";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken } from "firebase/app-check";
import { getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// // Check if Firebase is properly configured
const isFirebaseConfigured = () => {
  return firebaseConfig.apiKey &&
         !firebaseConfig.apiKey.includes('YOUR_') &&
         firebaseConfig.projectId &&
         !firebaseConfig.projectId.includes('YOUR_');
};

// Initialize Firebase
let app;
let db;
let appCheck = null;
let firebaseError = null;

try {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase not configured. Please add your Firebase config in src/firebase.js');
  }
  app = initializeApp(firebaseConfig);

  // ---- App Check ----
  // Attaches a reCAPTCHA Enterprise attestation token to every Firebase
  // request, which Firebase verifies server-side. Security rules say WHAT a
  // caller may read or write; they cannot say how often, or whether the
  // caller is this app at all. App Check is the piece that rejects a script
  // hitting the database REST endpoint directly.
  //
  // The Firebase SDK loads reCAPTCHA and calls grecaptcha.execute itself —
  // do NOT add Google's <script src="…/recaptcha/enterprise.js"> to
  // index.html or call execute by hand. Those instructions are for using
  // reCAPTCHA standalone with your own backend doing the assessment.
  //
  // Guarded on the key so a build without it behaves exactly as before.
  // Registering the app is only half of it: nothing is actually blocked
  // until enforcement is switched on per-service in the Firebase console,
  // and that should wait until the App Check metrics show real traffic
  // attesting cleanly.
  //
  // The site key must be a SCORE-BASED reCAPTCHA Enterprise key. A checkbox
  // ("challenge") key is a different product and App Check will never accept
  // it; the type is fixed at creation, so a wrong one has to be replaced
  // rather than reconfigured.
  if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
    // Its own try/catch, and that is the important part. This block used to
    // sit bare inside the outer try alongside getDatabase(), so anything that
    // made initializeAppCheck throw — a malformed key, a blocked reCAPTCHA
    // script, an ad blocker — skipped straight to the catch and left `db`
    // undefined. The whole app would then render its "Firebase not
    // configured" state because attestation failed. Attestation is a
    // hardening layer; losing it must never take the database with it.
    try {
      // Local dev never reaches reCAPTCHA. It uses a debug token instead —
      // a string you register under App Check -> Apps -> Manage debug tokens,
      // which Firebase then accepts in place of a real attestation.
      //
      // Setting this to `true` makes the SDK invent a random token per
      // browser and print it once. That is the documented flow and it is
      // genuinely awkward: the line is easy to miss (Chrome hides it behind
      // the console's "Verbose" level), it differs per browser and per
      // profile, and it is lost the moment site data is cleared.
      //
      // So prefer a token YOU choose: put any UUID in .env as
      // REACT_APP_APPCHECK_DEBUG_TOKEN, register that exact string once, and
      // every dev browser attests with no console archaeology. Falling back
      // to `true` keeps the stock behaviour when it isn't set.
      //
      // NEVER put this variable in a .env that gets deployed — it is a
      // deliberate bypass of App Check, and CRA inlines REACT_APP_* values
      // into the bundle where anyone can read them. check-env.js fails the
      // predeploy if it finds one.
      if (process.env.NODE_ENV !== 'production') {
        const fixedDebugToken = (process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || '').trim();
        // eslint-disable-next-line no-restricted-globals
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = fixedDebugToken || true;
        console.info(
          fixedDebugToken
            ? `[App Check] dev mode, using the fixed debug token from .env: ${fixedDebugToken}`
            : '[App Check] dev mode. The SDK will print a generated debug token below — if you cannot see it, set the console level to "All"/"Verbose", or set REACT_APP_APPCHECK_DEBUG_TOKEN in .env to a UUID of your choosing instead.',
        );
      }
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      // Unattested requests still succeed while the service is Unenforced,
      // so the app keeps working and the console says why it is unprotected.
      console.error('[App Check] failed to initialise — requests will go out UNATTESTED:', e?.message || e);
    }
  }

  db = getDatabase(app);
} catch (e) {
  firebaseError = e.message;
  console.error('Firebase Error:', e.message);
}

/**
 * Does this build actually attest? Asks App Check for a token and says so in
 * one line.
 *
 * This exists because of the order these things have to happen in. Registering
 * the app in the console changes nothing on its own; switching a service to
 * Enforced rejects every request that cannot produce a token. Enforce before a
 * build carrying the key is live and the site goes dark — including the admin
 * login, because AuthGuard talks to Firebase Auth on boot, so there is no
 * signed-in state left to turn enforcement back off from.
 *
 * So: deploy, run this check, and only then enforce.
 *
 * Runs automatically in development. In a production build it runs only when
 * the URL carries ?appcheck=1, so it costs a deployed guest nothing.
 */
const shouldProbeAppCheck = () => {
  if (process.env.NODE_ENV !== 'production') return true;
  try {
    return new URLSearchParams(window.location.search).get('appcheck') === '1';
  } catch (e) {
    return false;
  }
};

if (appCheck && shouldProbeAppCheck()) {
  getToken(appCheck, /* forceRefresh */ false)
    .then(() => {
      console.info('%c[App Check] OK — this build attests. Safe to switch services to Enforced.',
        'color:#2c5c4a;font-weight:700');
    })
    .catch((e) => {
      const code = e?.code || '';
      const key = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '(unset)';
      console.error('[App Check] NO TOKEN — do NOT enable enforcement yet. Reason:', e?.message || e);
      console.error('  hostname:', window.location.hostname, '| site key:', key);
      console.error('  project:', firebaseConfig.projectId, '| app id:', firebaseConfig.appId);

      if (code.includes('recaptcha-error')) {
        // reCAPTCHA itself refused. The SDK never says which of these it was,
        // so they are listed in the order they actually go wrong, and every
        // one of them is invisible on localhost — a registered debug token
        // satisfies App Check WITHOUT calling reCAPTCHA at all, which is why
        // a green line locally proves nothing about any of this.
        console.error('  reCAPTCHA rejected the call. In order of likelihood:');
        console.error('   1. The hostname above is not on the key\'s allowed-domains list.');
        console.error('      Firebase Hosting answers on BOTH <project>.web.app and');
        console.error('      <project>.firebaseapp.com — list both, plus any custom domain.');
        console.error('   2. The key is a checkbox/"challenge" key. App Check needs a SCORE-BASED');
        console.error('      key; the type is fixed at creation, so a wrong one must be replaced.');
        console.error('   3. The key above is not the one registered under App Check -> Apps.');
        console.error('   4. The reCAPTCHA Enterprise API (or billing) is off in the Cloud project.');
        console.error('   5. The key lives in a different Cloud project than', firebaseConfig.projectId + '.');
      } else if (code.includes('throttle')) {
        // A 400 from the exchange endpoint, then the SDK's own backoff.
        //
        // This one is easy to misread as "still broken the same way". It is
        // not: reaching here means reCAPTCHA MINTED a token successfully and
        // the App Check backend then refused to exchange it. reCAPTCHA is
        // working; the disagreement is between the token's key and what the
        // backend expects for this app.
        console.error('  reCAPTCHA produced a token; App Check refused to exchange it (HTTP 400).');
        console.error('  In Firebase Console -> App Check -> Apps, find the row for app id');
        console.error('   ', firebaseConfig.appId);
        console.error('  and check, in this order:');
        console.error('   1. It is registered with reCAPTCHA Enterprise and the key saved there is');
        console.error('      exactly', key + '. Creating a new key does NOT update this row.');
        console.error('   2. It is THAT app id. Registration is per web app, and a project with');
        console.error('      more than one web app happily lets you register the wrong one.');
        console.error('   3. The reCAPTCHA Enterprise API is enabled in the Cloud project, and the');
        console.error('      key lives in', firebaseConfig.projectId + ', not another project.');
        console.error('  The backoff is in-memory only — fix the config, then hard-reload.');
      } else if (code.includes('fetch-status-error') || code.includes('fetch-network-error')) {
        console.error('  Could not reach the App Check backend — a blocked request, an ad blocker,');
        console.error('  or the Firebase App Check API not enabled for this project.');
      } else {
        console.error('  On localhost: register the debug token printed above under');
        console.error('  App Check -> Apps -> Manage debug tokens.');
      }
      console.error('  Nothing is broken while every service is still Unenforced — requests');
      console.error('  simply go out unattested. Fix this before switching anything to Enforced.');
    });
} else if (!appCheck && process.env.NODE_ENV !== 'production') {
  console.warn('[App Check] inactive — REACT_APP_RECAPTCHA_SITE_KEY is not set in this build.');
}

// Initialize Firebase
//const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const auth = getAuth(app); // 👈 Added 'export' here
export const storage = getStorage(app);
// appCheck is null when the key is absent or initialisation failed — check
// it before assuming requests are attested.
export { db, appCheck, firebaseError }; // Export database and error for use in other parts of the app

// Optional: If you ever need the raw app instance elsewhere
export default app;